"use client"

import * as React from "react"
import {
  IconArrowRotateRightLeft,
  IconCheckmark1Medium,
  IconMagnifyingGlass,
} from "@central-icons-react/round-outlined-radius-3-stroke-2"
import { Slot as SlotPrimitive } from "radix-ui"

import { Tooltip as TooltipPrimitive } from "radix-ui"

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { Button } from "@/components/ui/button"
import { OverlayScrollbar } from "@/components/ui/overlay-scrollbar"
import { cn } from "@/lib/utils"

export const popoverMenuSideOffset = 4
export const popoverMenuCollisionPadding = 12
/** Gap between primary and secondary panel edges. */
export const popoverMenuSubmenuSideOffset = 4
/** Matches horizontal inset from `p-1` on `popoverMenuSurfaceClass`. */
export const popoverMenuInsetPx = 4
/** Radix `sideOffset` from a full-width menu row; includes panel inset + panel gap. */
export const popoverMenuSubmenuTriggerSideOffset =
  popoverMenuInsetPx + popoverMenuSubmenuSideOffset

export const popoverMenuSurfaceClass = cn(
  "flex flex-col items-stretch justify-center gap-0 rounded-[10px] border border-[#eee] bg-neutral-01 p-1 text-neutral-12 shadow-(--shadow-3) ring-0",
)

/** Neutralize PopoverContent shell styles so only `popoverMenuSurfaceClass` shows. */
const popoverMenuPanelResetClass = cn(
  "gap-0 rounded-none border-0 bg-transparent p-0 shadow-none ring-0",
)

export const popoverMenuIconTriggerClass = cn(
  "inline-flex size-[24px] shrink-0 items-center justify-center rounded-lg p-0",
  "outline-none transition-[transform,background-color] duration-150 ease-[cubic-bezier(0.23,1,0.32,1)]",
  "hover:bg-black/4 focus-visible:bg-black/4 data-[state=open]:bg-black/4",
  "active:scale-[0.97] motion-reduce:active:scale-100",
  "disabled:pointer-events-none disabled:opacity-40",
)

export const popoverMenuItemClass = cn(
  "flex w-full self-stretch items-center gap-2 rounded-[6px] py-1.5 px-2 text-left font-caption-1-book text-neutral-12",
  "outline-none transition-[transform,background-color] duration-150 ease-[cubic-bezier(0.23,1,0.32,1)] hover:bg-black/4 focus-visible:bg-black/4 active:scale-[0.98] motion-reduce:transition-colors motion-reduce:active:scale-100 disabled:pointer-events-none disabled:opacity-50",
)

export const popoverMenuSearchInputClass = cn(
  "min-w-0 flex-1 bg-transparent font-caption-1-book text-neutral-12 outline-none placeholder:text-neutral-07",
)

export const popoverMenuSearchFieldClass = cn(
  "flex items-center gap-2 px-2 py-1.5",
)

export const popoverMenuSearchSectionClass = cn("w-full px-1 pt-1 pb-0")

const popoverMenuRefreshEaseOut = "cubic-bezier(0.23, 1, 0.32, 1)"
const popoverMenuRefreshDurationMs = 1000

export type PopoverMenuRefreshIconButtonProps = {
  loading: boolean
  disabled?: boolean
  onRefresh?: () => void
  ariaLabel?: string
}

function PopoverMenuRefreshIconButton({
  loading,
  disabled,
  onRefresh,
  ariaLabel = "Refresh",
}: PopoverMenuRefreshIconButtonProps) {
  const iconRef = React.useRef<SVGSVGElement>(null)
  const refreshAnimationRef = React.useRef<Animation | null>(null)

  React.useEffect(() => {
    if (!loading) return

    const icon = iconRef.current
    if (!icon) return

    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches

    refreshAnimationRef.current?.cancel()
    icon.style.transform = ""

    if (prefersReducedMotion) {
      icon.style.opacity = "0.55"
      return
    }

    const animation = icon.animate(
      [{ transform: "rotate(0deg)" }, { transform: "rotate(360deg)" }],
      {
        duration: popoverMenuRefreshDurationMs,
        easing: popoverMenuRefreshEaseOut,
        fill: "forwards",
      },
    )
    refreshAnimationRef.current = animation
    void animation.finished.finally(() => {
      if (iconRef.current) {
        iconRef.current.style.transform = ""
        iconRef.current.style.opacity = ""
      }
      refreshAnimationRef.current = null
    })
  }, [loading])

  React.useEffect(() => {
    if (loading) return
    const icon = iconRef.current
    if (icon) icon.style.opacity = ""
  }, [loading])

  React.useEffect(() => {
    return () => {
      refreshAnimationRef.current?.cancel()
    }
  }, [])

  return (
    <button
      type="button"
      aria-label={ariaLabel}
      aria-busy={loading}
      disabled={disabled || loading}
      onClick={() => onRefresh?.()}
      className={popoverMenuIconTriggerClass}
    >
      <IconArrowRotateRightLeft
        ref={iconRef}
        className="size-4 text-neutral-08"
        aria-hidden
      />
    </button>
  )
}

const popoverMenuItemDestructiveClass = cn(
  "text-[#d30505] data-[variant=destructive]:hover:bg-[#d30505]/8 data-[variant=destructive]:focus-visible:bg-[#d30505]/8",
)

function PopoverMenuContent({
  className,
  onOpenAutoFocus,
  ...props
}: React.ComponentProps<typeof PopoverContent>) {
  return (
    <PopoverContent
      {...props}
      className={cn(
        popoverMenuPanelResetClass,
        popoverMenuSurfaceClass,
        "w-[10.75rem]",
        className,
      )}
      onOpenAutoFocus={(event) => {
        onOpenAutoFocus?.(event)
        if (!event.defaultPrevented) {
          event.preventDefault()
        }
      }}
    />
  )
}

export type PopoverMenuDividerProps = React.ComponentProps<"div"> & {
  /**
   * `inset` keeps the rule aligned with menu item padding (default).
   * `full` spans edge-to-edge inside the padded panel for separating sections.
   */
  variant?: "inset" | "full"
}

function PopoverMenuDivider({
  className,
  variant = "inset",
  ...props
}: PopoverMenuDividerProps) {
  return (
    <div
      aria-hidden="true"
      role="separator"
      className={cn(
        "h-px shrink-0 bg-[#eee]",
        variant === "inset" && "mx-1",
        variant === "full" && "-mx-1",
        className,
      )}
      {...props}
    />
  )
}

function PopoverMenuSection({
  className,
  fullWidthDividerAfter,
  children,
  ...props
}: React.ComponentProps<"div"> & {
  /** Renders a full-bleed divider after this block (into the menu panel padding). */
  fullWidthDividerAfter?: boolean
}) {
  return (
    <div className={cn("w-full py-0", className)} {...props}>
      {children}
      {fullWidthDividerAfter ? (
        <PopoverMenuDivider variant="full" className="my-1" />
      ) : null}
    </div>
  )
}

/**
 * Wheel handler for the scrolling viewport when the menu is portaled out of
 * a Radix Dialog: react-remove-scroll's document-level capture listener
 * doesn't recognize portaled popovers as nested scrollers, so wheel scroll
 * silently dies inside any picker opened from a modal. Runs in capture phase
 * first, drives the scroll manually, and stops propagation — except at the
 * edges, where the event escapes so the page-level lock can no-op normally.
 * Attached to the VIEWPORT (the element that actually scrolls); on the outer
 * wrapper it always reads as at-edge and no-ops.
 */
function onScrollSectionWheelCapture(e: React.WheelEvent<HTMLElement>): void {
  const el = e.currentTarget
  const atTop = el.scrollTop <= 0
  const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight <= 0
  if ((e.deltaY < 0 && atTop) || (e.deltaY > 0 && atBottom)) {
    return
  }
  el.scrollTop += e.deltaY
  e.stopPropagation()
  e.preventDefault()
}

function PopoverMenuScrollSection({
  className,
  maxHeightClassName = "max-h-56",
  children,
  ...props
}: React.ComponentProps<"div"> & {
  maxHeightClassName?: string
}) {
  return (
    <PopoverMenuSection {...props} className="overflow-hidden">
      <OverlayScrollbar
        className={cn("w-full overflow-hidden", maxHeightClassName)}
        viewportClassName={cn(
          "flex flex-col overflow-y-auto",
          maxHeightClassName,
          className,
        )}
        viewportProps={{ onWheelCapture: onScrollSectionWheelCapture }}
        trackInset={3}
        minThumb={20}
      >
        {children}
      </OverlayScrollbar>
    </PopoverMenuSection>
  )
}

export type PopoverMenuSearchSectionProps = React.ComponentProps<"div"> & {
  /** Renders a full-bleed divider after this block (into the menu panel padding). */
  fullWidthDividerAfter?: boolean
  /** Defaults to `IconMagnifyingGlass`. Pass `null` to hide. */
  icon?: React.ReactNode | null
  /** Optional trailing control (e.g. refresh button). */
  trailing?: React.ReactNode
  inputProps: React.ComponentProps<"input">
}

function PopoverMenuSearchSection({
  className,
  fullWidthDividerAfter,
  icon,
  trailing,
  inputProps,
  ...props
}: PopoverMenuSearchSectionProps) {
  const searchIcon =
    icon === null
      ? null
      : (icon ?? (
          <IconMagnifyingGlass
            className="size-4 shrink-0 text-neutral-08"
            aria-hidden
          />
        ))

  return (
    <div className={cn(popoverMenuSearchSectionClass, className)} {...props}>
      <div className={popoverMenuSearchFieldClass}>
        <label className="flex min-w-0 flex-1 items-center gap-2">
          {searchIcon}
          <input
            {...inputProps}
            className={cn(popoverMenuSearchInputClass, inputProps.className)}
          />
        </label>
        {trailing}
      </div>
      {fullWidthDividerAfter ? (
        <PopoverMenuDivider variant="full" className="my-1" />
      ) : null}
    </div>
  )
}

export type PopoverMenuItemProps = React.ComponentProps<"button"> & {
  asChild?: boolean
  /** Optional leading icon (e.g. 16px); omit for text-only rows. */
  icon?: React.ReactNode
  label: React.ReactNode
  /** Optional content between the label and the selection check (e.g. shortcut). */
  trailing?: React.ReactNode
  /** When true, shows a checkmark on the far right (e.g. current model in a picker). */
  selected?: boolean
  /** `destructive` paints the row red (Delete, Remove, etc.). */
  variant?: "default" | "destructive"
}

function PopoverMenuItem({
  asChild,
  className,
  icon,
  label,
  trailing,
  selected,
  variant = "default",
  type = "button",
  ...props
}: PopoverMenuItemProps) {
  const Comp = asChild ? SlotPrimitive.Slot : "button"
  const showTrailingSlot = trailing != null || selected

  return (
    <Comp
      className={cn(
        popoverMenuItemClass,
        variant === "destructive" && popoverMenuItemDestructiveClass,
        className,
      )}
      type={asChild ? undefined : type}
      data-selected={selected ? "true" : undefined}
      data-variant={variant}
      {...props}
    >
      {icon ? (
        <span className="flex size-4 shrink-0 items-center justify-center">
          {icon}
        </span>
      ) : null}
      <span className="min-w-0 flex-1 truncate text-left">{label}</span>
      {showTrailingSlot ? (
        <span className="flex shrink-0 items-center gap-1.5">
          {trailing}
          {selected ? (
            <IconCheckmark1Medium
              size={16}
              color="currentColor"
              className="shrink-0 text-neutral-11"
              aria-hidden
            />
          ) : null}
        </span>
      ) : null}
    </Comp>
  )
}

function PopoverMenuSubmenuContent({
  className,
  side = "right",
  align = "start",
  sideOffset = popoverMenuSubmenuTriggerSideOffset,
  collisionPadding = popoverMenuCollisionPadding,
  children,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content>) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        side={side}
        align={align}
        sideOffset={sideOffset}
        collisionPadding={collisionPadding}
        className={cn(
          popoverMenuSurfaceClass,
          "z-[calc(var(--z-popover)+1)] max-w-none outline-hidden",
          className,
        )}
        {...props}
      >
        {children}
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  )
}

const PopoverMenuIconTrigger = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<"button">
>(function PopoverMenuIconTrigger(
  { className, type = "button", ...props },
  ref,
) {
  return (
    <Button
      ref={ref}
      type={type}
      variant="subtle"
      size="icon"
      className={cn(popoverMenuIconTriggerClass, className)}
      {...props}
    />
  )
})
PopoverMenuIconTrigger.displayName = "PopoverMenuIconTrigger"

type PopoverMenuProps = Omit<
  React.ComponentProps<typeof Popover>,
  "children"
> & {
  /** Optional content to the left of the trigger (e.g. title). */
  leading?: React.ReactNode
  leadingContainerClassName?: string
  rowClassName?: string
  /** Single element passed to `PopoverTrigger` (`asChild`). */
  trigger: React.ReactElement
  /** Panel body; omit or pass `null` to skip rendering the popover surface (e.g. while loading). */
  menu?: React.ReactNode | null
  menuContentClassName?: string
  menuContentProps?: Omit<
    React.ComponentProps<typeof PopoverMenuContent>,
    "children"
  >
}

function PopoverMenu({
  leading,
  leadingContainerClassName,
  rowClassName,
  trigger,
  menu,
  menuContentClassName,
  menuContentProps,
  ...popoverProps
}: PopoverMenuProps) {
  const hasMenu = menu != null && menu !== false
  const { className: menuContentClassFromProps, ...restMenuContentProps } =
    menuContentProps ?? {}

  return (
    <Popover {...popoverProps}>
      <span
        className={cn(
          "inline-flex min-w-0 max-w-full items-center text-inherit",
          rowClassName,
        )}
      >
        {leading != null ? (
          <span
            className={cn(
              "min-w-0 max-w-[min(16rem,calc(100vw-12rem))] px-2 py-1",
              leadingContainerClassName,
            )}
          >
            {leading}
          </span>
        ) : null}
        <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      </span>
      {hasMenu ? (
        <PopoverMenuContent
          align="start"
          collisionPadding={popoverMenuCollisionPadding}
          side="bottom"
          sideOffset={popoverMenuSideOffset}
          {...restMenuContentProps}
          className={cn(menuContentClassFromProps, menuContentClassName)}
        >
          {menu}
        </PopoverMenuContent>
      ) : null}
    </Popover>
  )
}

export {
  PopoverMenu,
  PopoverMenuContent,
  PopoverMenuDivider,
  PopoverMenuIconTrigger,
  PopoverMenuItem,
  PopoverMenuRefreshIconButton,
  PopoverMenuSearchSection,
  PopoverMenuScrollSection,
  PopoverMenuSection,
  PopoverMenuSubmenuContent,
}
